import { Component, h, Prop, State, Element, Event, Listen, EventEmitter, Watch, Method } from '@stencil/core';

import '@ionic/core';

import { TranslationUtils } from '../../lang/i18n';
import parser from "ua-parser-js";
import { modalController } from '@ionic/core';

// import amwalLogo from './assets/logo-white.svg';
// import madaLogo from './assets/mada-light-2.svg';
import applePayLogo from './assets/Apple_Pay_logo.svg';
// import visaLogo from './assets/Visa_Inc._logo.svg';
import cardLogo from './assets/card-logo.svg';
// import masterLogo from './assets/Mastercard-logo.svg';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js';

const showBrandingCache: Record<string, any> = {};

declare global {
  interface Window { ApplePaySession: any; }
}

export interface AmwalCheckoutStatus {
  orderId: string;
}

export interface AmwalDismissalStatus {
  orderId: string;
  paymentSuccessful: boolean;
}

export interface IShippingMethod {
  id: string;
  label: string;
  price: number;
}

export interface StateSpecs {
  [key: string]: string;
}

export interface CitySpecs {
  [key: string]: Array<string>;
}

export interface ICountry {
  addressCountry: string;
}

export interface IState {
  addressCountry: string;
  addressState: string;
}

export interface IAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

export interface IShipping {
  id: string;
  label: string;
  price: number;
}

export interface ITransactionDetails {
  id: string;
  address_details: IAddress;
  shipping_details: IShipping;
  card_bin?: string;
}

export interface IErrorDescription {
  description: string;
}

export interface IOrder {
  order_id: string;
  order_total_amount: number;
  card_bin_additional_discount?: number;
  card_bin_additional_discount_message?: string;
}

export interface IOrderDetails {
  order_position: string;
  order_content: Array<string>;
  plugin_version: string;
  locale?: string;
}

interface PublicKeyCredentialFuture extends PublicKeyCredential {
  isConditionalMediationAvailable?(): Promise<boolean>;
}

@Component({
  tag: 'amwal-checkout-button',
  styleUrl: 'amwal-checkout-button.css',
})
export class AmwalCheckoutButton {
  // private showBranding = false;
  private requirePopup = false;
  private showButton = this.debug;
  @State() hideIcons = true;
  private resizeObserver: ResizeObserver;

  /**
   * merchantId is the key to use obtained from https://merchant.sa.amwal.tech
   */
  @Prop() merchantId: string;

  /**
   * The installment options url is URL for the checkout page in the merchant website
   */
  @Prop() installmentOptionsUrl: string;

  /**
   * The amount of money to charge the customer
   */
  @Prop() amount: number;

  /**
   * The country code of the merchant (e.g. SA)
   */
  @Prop() countryCode: CountryCode = "SA";

  /**
   * the language of the plugin. supported languages are Arabic ("ar") and English ("en")
   */
  @Prop() locale?: string = "en";

  /**
   * the label of the button. Either checkout for final checkout or quicky-buy for per product buy
   */
  @Prop() label?: "Quick Checkout" | "Bank Installments" | "Pay with Apple Pay" = "Quick Checkout";

  /**
   * Dark Mode:
   *  - on: turns on dark mode
   *  - off: turns on light mode
   *  - auto: use customer preference for dark mode
   */
  @Prop() darkMode?: "on" | "off" | "auto" = "off";

  /**
   * disables the checkout button
   */
  @Prop() disabled?: boolean = false;

  /**
   * addressRequired specifies if shipping address is required or not ("true" or "false")
   */
  @Prop() addressRequired?: boolean = true;


  /**
   * showPaymentBrands to show supporeted brands under button values are ("true" or "false")
   */
  @Prop() showPaymentBrands?: boolean = false;

  /**
   * showDiscountRibbon to show discount ribbon values are ("true" or "false")
   */
  @Prop() showDiscountRibbon?: boolean = false;

  /**
   * initialAddress specifies a default address, Country field is country code (two letter ISO country code)
   */
  @Prop() initialAddress?: IAddress;

  /**
   * allowedAddressCountries specifies an array of allowed countries for the address
   */
  @Prop() allowedAddressCountries?: string[];

  /**
   * allowedAddressStates specifies an array of allowed states for the address example {"SA":{"XX1":"State 1","XX2":"State 2"},"US":{"US1":"State 1","US2":"State 2"}}
   */
  @Prop() allowedAddressStates?: { [index: string]: StateSpecs };

  /**
   * allowedAddressCities specifies an array of allowed states for the address example {"SA":{"XX1":["Riady","Afif"],"XX2":["Jeddah","Makkah"]},"US":{"US1":["San Jose","San Francisco"],"US2":["New york"]}}
   */
  @Prop() allowedAddressCities?: { [index: string]: CitySpecs };

  /**
   * street2Required specifies if street 2 is required or not ("true" or "false"). Can only be true if addressRequired is true.
   */
  @Prop({ attribute: 'street2-required' }) street2Required?: boolean = false;

  /**
   * emailRequired specifies if email address is required or not ("true" or "false"). Can only be true if addressRequired is true.
   */
  @Prop() emailRequired?: boolean = true;

  /**
   * initialEmail specifies a default email
   */
  @Prop() initialEmail?: string;

  /**
   * initialFirstName specifies a default first name
   */
  @Prop() initialFirstName?: string;

  /**
   * initialLastName specifies a default last name
   */
  @Prop() initialLastName?: string;

  /**
   * initialPhone specifies a default phone number
   */
  @Prop() initialPhoneNumber?: string;

  /**
   * addressHandshake specifies if address event and ack is activated ("true" or "false")
   */
  @Prop() addressHandshake?: boolean = false;

  /**
   * enableDynamicShippingDetails specifies if shipping countries, states and cities will load dynamically or prefilled in the parameters
   */
  @Prop() enableDynamicShippingDetails?: boolean = false;

  /**
   * sendExtraAddressFields specifies if extra address fields are sent in amwalAddressUpdate event
   */
  @Prop() sendExtraAddressFields?: boolean = false;

  /**
   * shippingMethods an array of shipping methods
   */
  @Prop() shippingMethods?: IShippingMethod[] = [];

  /**
   * taxes specifies any additional taxes
   */
  @Prop() taxes?: number = 0;

  /**
   * discount specifies any discount to be applied
   */
  @Prop() discount?: number = 0;


  /**
   * discountDescription provides description for the discount
   */
  @Prop() discountDescription?: string;

  /**
   * fees specifies any fees to be applied
   */
  @Prop() fees?: number = 0;

  /**
   * feesDescription provides description for the fees
   */
  @Prop() feesDescription?: string;

  /**
   * refId an identifier to be included in the payment transaction
   */
  @Prop() refId?: string;

  /**
   * uniqueRef a boolean to enforce the uniqueness of refId
   */
  @Prop() uniqueRef?: boolean = false;

  /**
   * onlyShowBankInstallment specifies if only bank installments should be shown
   */
  @Prop() onlyShowBankInstallment?: boolean = false;

  /**
   * enableAppleCheckout specifies if Apple Checkout is enabled
   */
  @Prop() enableAppleCheckout?: boolean = false;

  /**
   * Custom api URL (optional). If not provided, uses process.env.BACKEND_URL
   * Example: 'https://qa.amwal.dev'
   */
  @Prop() apiUrl?: string;

  /**
   * Custom payment page URL (optional). If not provided, uses process.env.PAY_URL
   * Example: 'https://pay.amwal.dev'
   */
  @Prop() payUrl?: string;

  /**
   *  amwalCheckoutSuccess is a dom event fired on success of payment. the event has orderId field which can be used
   *  to lookup transaction status at https://backend.sa.amwal.tech
   */
  @Event() amwalCheckoutSuccess: EventEmitter<AmwalCheckoutStatus>;

  /**
   *  updateOrderOnPaymentsuccess is a dom event fired on success of payment. the event has orderId field which can be used
   *  to lookup transaction status at https://backend.sa.amwal.tech
   */
  @Event() updateOrderOnPaymentsuccess: EventEmitter<AmwalCheckoutStatus>;

  /**
   *  amwalDismissed is a dom event fired when the amwal modal or popup window is dismissed, event.detail indicates if it's successfull or not (boolean)
   */
  @Event() amwalDismissed: EventEmitter<AmwalDismissalStatus>;

  /**
   *  amwalAddressUpdate is a dom event fired on address selection.
   */
  @Event() amwalAddressUpdate: EventEmitter<IAddress>;

  /**
   *  amwalAddressCountryUpdate is a dom event fired on address country selection.
   */
  @Event() amwalAddressCountryUpdate: EventEmitter<ICountry>;

  /**
   *  amwalAddressStateUpdate is a dom event fired on address country selection.
   */
  @Event() amwalAddressStateUpdate: EventEmitter<IState>;

  /**
   * debug flag for verbose printing of debug messages
   */
  @Prop() debug?: boolean = false;

  /**
   * debug flag for verbose printing of debug messages
   */
  @Prop() showContinueShoppingButton?: boolean = false;

  /**
   * enable triggering a pre pay event to do final task such as order creation
   */
  @Prop() enablePrePayTrigger?: boolean = false;

  /**
   *  amwalPrePayTrigger is a dom event fired once payment button is clicked before payment is processed. It is
   *  enabled if enablePrePayTrigger is set. It waits for either amwalPrePayTriggerAck or amwalPrePayTriggerError
   *  events to be sent back before proceeding to payment or aborting respectively.
   */
  @Event() amwalPrePayTrigger: EventEmitter<ITransactionDetails>;

  /**
   * enable triggering a pre checkout click event to do initial tasks such as adding items to carts
   */
  @Prop() enablePreCheckoutTrigger?: boolean = false;

  /**
   *  amwalPreCheckoutTrigger is a dom event fired once amwal checkout button is clicked before a transaction is created. It is
   *  enabled if enablePreCheckoutTrigger is set. It waits for either amwalPreCheckoutTriggerAck or amwalPreCheckoutTriggerError
   *  events to be sent back before proceeding with the normal checkout flow.
   */
  @Event() amwalPreCheckoutTrigger: EventEmitter<ITransactionDetails>;

  /**
   * enable postal code required option
   */
  @Prop() postcodeOptionalCountries?: string[];

  /**
   * Number of installments
   */
  @Prop() installmentsCount: number = 6;

  /**
   *  * timelineStyle specifies the style of the timeline in the checkout modal.
   */
  @Prop() timelineStyle: 'default' | 'simple' = 'default';

  /**
   *  * timelineFooterText specifies the text to be displayed in the footer of the timeline in the checkout modal.
   *
   */
  @Prop() footerMessage: string = '';

  private waitingForPreCheckoutTriggerAck = false;

  /**
   * Get the backend URL - uses custom apiUrl prop if provided, otherwise falls back to process.env.BACKEND_URL
   */
  private getApiUrl(): string {
    return this.apiUrl ||  (typeof process !== 'undefined' ? process.env.BACKEND_URL : null) || 'https://backend.sa.amwal.tech';
  }

  /**
   * Get the payment page URL - uses custom payUrl prop if provided, otherwise falls back to process.env.PAY_URL
   */
  private getPayUrl(): string {
    return this.payUrl || (typeof process !== 'undefined' ? process.env.PAY_URL : null) || 'https://pay.sa.amwal.tech';
  }

  @Listen('amwalPreCheckoutTriggerAck')
  prePayCheckoutAckHandler(event: CustomEvent<IOrderDetails>) {
    this.debugMsg("Pre Checkout Trigger Ack Received");
    this.debugMsg("Current Amount: ", this.amount);
    this.debugMsg("Current Taxes: ", this.taxes);
    this.debugMsg("Current Discount: ", this.discount);
    this.debugMsg("Current Fees: ", this.fees);
    this.debugMsg("Order Details: ", event.detail);
    event.detail.locale = this.locale
    if (this.waitingForPreCheckoutTriggerAck) {
      this.waitingForPreCheckoutTriggerAck = false;
      this.createTransaction({ ...event.detail });
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalPreCheckoutTriggerAck");
    }
  }

  @Listen('amwalPreCheckoutTriggerError')
  prePayCheckoutErrorHandler(event: CustomEvent<IErrorDescription>) {
    this.debugMsg(`Pre Checkout Trigger Error Received: ${event.detail}`);
    if (this.waitingForPreCheckoutTriggerAck) {
      this.waitingForPreCheckoutTriggerAck = false;
      this.orderError = event.detail;
      this.sendOrderDetails();
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalPreCheckoutTriggerError");
    }
  }

  private waitingForPrePayTriggerAck = false;

  @Listen('amwalPrePayTriggerAck')
  prePayTriggerAckHandler(event: CustomEvent<IOrder>) {
    this.debugMsg("Pre Pay Trigger Ack Received");
    this.debugMsg("Current Shipping Methods: ", this.shippingMethods);
    this.debugMsg("Current Taxes: ", this.taxes);
    this.debugMsg("Current Discount: ", this.discount);
    this.debugMsg("Current Fees: ", this.fees);
    if (this.waitingForPrePayTriggerAck) {
      this.waitingForPrePayTriggerAck = false;
      this.payWindow?.postMessage({
        type: 'pre-pay-trigger-ack',
        shippingMethods: this.shippingMethods,
        taxes: this.taxes,
        discount: this.discount,
        discountDescription: this.discountDescription,
        fees: this.fees,
        feesDescription: this.feesDescription,
        amount: this.amount,
        ...event.detail
      }, '*');
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalPrePayTriggerAck");
    }
  }

  @Listen('amwalPrePayTriggerError')
  prePayTriggerErrorHandler(event: CustomEvent<IErrorDescription>) {
    this.debugMsg("Pre Pay Trigger Error Received");
    if (this.waitingForPrePayTriggerAck) {
      this.waitingForPrePayTriggerAck = false;
      this.payWindow?.postMessage({
        type: 'pre-pay-trigger-error',
        ...event.detail,
      }, '*');
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalPrePayTriggerError");
    }
  }

  @Watch('shippingMethods')
  parseMyArrayProp(newValue: string, _oldValue: string) {
    const newVal = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
    if (Array.isArray(newVal)) {
      this.debugMsg("Setting shippingMethods to:", newVal);
      this.shippingMethods = newVal;
    } else {
      this.debugMsg("Failed to set shippingMethods to:", newVal);
    }
  }

  @Watch('initialAddress')
  parseInitiaalAddressProp(newValue: string, _oldValue: string) {
    this.initialAddress = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
  }

  @Watch('allowedAddressCountries')
  parseAllowedAddressCountries(newValue: string, _oldValue: string) {
    const newVal = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
    if (Array.isArray(newVal)) {
      this.debugMsg("Setting allowedAddressCountries to:", newVal);
      this.allowedAddressCountries = newVal;
    } else {
      this.debugMsg("Failed to set allowedAddressCountries to:", newValue);
    }
  }

  @Watch('postcodeOptionalCountries')
  parsepostcodeOptionalCountries(newValue: string, _oldValue: string) {
    const newVal = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
    if (Array.isArray(newVal)) {
      this.debugMsg("Setting postcodeOptionalCountries to:", newVal);
      this.postcodeOptionalCountries = newVal;
    } else {
      this.debugMsg("Failed to set postcodeOptionalCountries to:", newValue);
    }
  }

  @Watch('allowedAddressStates')
  parseAllowedAddressStates(newValue: string, _oldValue: string) {
    const newVal = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
    if (newVal) {
      this.debugMsg("Setting allowedAddressStates to:", newVal);
      this.allowedAddressStates = newVal;
    } else {
      this.debugMsg("Failed to set allowedAddressStates to:", newValue);
    }
  }

  @Watch('allowedAddressCities')
  parseAllowedAddressCities(newValue: string, _oldValue: string) {
    const newVal = typeof newValue === 'string'? JSON.parse(newValue) : newValue;
    if (newVal) {
      this.debugMsg("Setting allowedAddressCities to:", newVal);
      this.allowedAddressCities = newVal;
    } else {
      this.debugMsg("Failed to set allowedAddressCities to:", newValue);
    }
  }

  @Watch('locale')
  async updateLocale(newValue: string) {
    this.locale = newValue;
    this.t = await TranslationUtils.useTranslation(this.locale);
  }

  @Watch('discount')
  validateDiscount(newValue: number, _oldValue: number) {
    const isNumber = typeof newValue === 'number';
    if (isNumber) {
      if (newValue >= this.amount) {
        throw new Error('Discount must be less than amount field')
      }
    } else {
      throw new Error('Discount has to be a number')
    }
  }

  @Watch('fees')
  validateFees(newValue: number, _oldValue: number) {
    const isNumber = typeof newValue === 'number';
    if (!isNumber) {
      throw new Error('Fees has to be a number')
    }
  }

  private agent = parser(window.navigator.userAgent);

  @Element() el: HTMLElement;

  private checkoutModalEl!: HTMLIonModalElement;
  private payWindow?: Window;
  private t: (val: string) => string;
  private useModal: boolean;
  private orderError?: IErrorDescription;
  @State() result: any;

  @State() paymentSuccessful = false;
  @State() orderId = "";
  @State() dark: boolean;
  @State() internallyDisabled: boolean = false;

  prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

  toggleDark(matches: boolean) {
    this.dark = this.darkMode == 'on' || (this.darkMode == 'auto' && matches);
  }

  private debugMsg(...args) {
    if (this.debug) {
      console.log(...args);
    }
  }

  async componentWillLoad() {
    // https://w3c.github.io/webauthn/#dom-publickeycredential-isconditionalmediationavailable
    const cacheKey = `${this.merchantId}-${this.countryCode}`;
    let config = showBrandingCache[cacheKey];
    if (!config) {
      try {
        const fromSession = sessionStorage.getItem('amwal-showbrand-' + cacheKey);
        if (fromSession) {
          config = JSON.parse(fromSession);
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (!config) {
      try {
        const response = await fetch(`${this.getApiUrl()}/showbrand/`, {
          headers: {
            'Content-Type': 'application/json',
            'Amwal': this.merchantId,
          }
        });
        if (response.ok) {
          config = await response.json();
          showBrandingCache[cacheKey] = config;
          try {
            sessionStorage.setItem('amwal-showbrand-' + cacheKey, JSON.stringify(config));
          } catch (e) {
            console.log(e);
          }
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (config) {
      // this.showBranding = config.SHOW_BRANDING;
      this.requirePopup = config.REQUIRE_POPUP && (window.top !== window.self);
      this.showButton = true;
    }

    const publickeycredential = window?.PublicKeyCredential as unknown as PublicKeyCredentialFuture
    const isConditionalMediationAvailable = publickeycredential?.isConditionalMediationAvailable;
    const browserSupportsWebauthnAutofill = isConditionalMediationAvailable ? await isConditionalMediationAvailable() : false;
    const browserName = this.agent.browser.name;
    const osName = this.agent.os.name;
    const firefoxOnMac = browserName === "Firefox" && osName == "Mac OS";
    const chromeOnNonIOS = ((browserName === "Chrome" || browserName === "Edge") && osName !== "iOS");
    const supportedBrowsers = ["Chrome", "Edge", "Firefox", "Safari"];
    const mobileOnSocialMediaBrowsers = (osName === "iOS" || osName === "Android") && !supportedBrowsers.includes(browserName);
    //const useAppleCheckout = this.enableAppleCheckout && this.isApplePaySupported()
    this.useModal = !this.requirePopup && !this.isApplePaySupported() &&
        (  browserSupportsWebauthnAutofill
            || chromeOnNonIOS
            || firefoxOnMac
            || mobileOnSocialMediaBrowsers);
    this.toggleDark(this.prefersDark.matches);
    this.prefersDark.addEventListener('change', (e) => this.toggleDark(e.matches));
    this.t = await TranslationUtils.useTranslation(this.locale);
    this.shippingMethods = JSON.parse(this.el.getAttribute('shipping-methods'));
    this.initialAddress = JSON.parse(this.el.getAttribute('initial-address'));
    this.allowedAddressCountries = JSON.parse(this.el.getAttribute('allowed-address-countries'));
    this.postcodeOptionalCountries = JSON.parse(this.el.getAttribute('postcode-optional-countries'));
    this.allowedAddressStates = JSON.parse(this.el.getAttribute('allowed-address-states'));
    this.allowedAddressCities = JSON.parse(this.el.getAttribute('allowed-address-cities'));
  }

  componentDidLoad() {
    this.computeHideIcons(this.el.offsetWidth);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.resizeObserver = new ResizeObserver(entries =>
        entries.forEach(entry => {
          this.computeHideIcons(entry.contentRect.width);
        })
    );
    this.resizeObserver.observe(this.el.querySelector('ion-button').shadowRoot.querySelector('button'));
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }


  private sendOrderDetails() {
    if (this.payWindow && !this.paymentSuccessful) {
      if (this.orderError) {
        this.payWindow.postMessage({
          type: 'amwal-order-error',
          ...this.orderError,
        }, '*');
      } else if (this.orderId) {
        const orderDetails = {
          type: 'amwal-order-id',
          id: this.orderId,
          darkMode: this.darkMode,
          lang: this.locale,
          addressRequired: this.addressRequired,
          street2Required: this.street2Required,
          emailRequired: this.emailRequired,
          addressHandshake: this.addressHandshake,
          sendExtraAddressFields: this.sendExtraAddressFields,
          enableDynamicShippingDetails: this.enableDynamicShippingDetails,
          enablePrePayTrigger: this.enablePrePayTrigger,
          shippingMethods: this.shippingMethods,
          taxes: this.taxes,
          discount: this.discount,
          discountDescription: this.discountDescription,
          fees: this.fees,
          feesDescription: this.feesDescription,
          initialEmail: this.initialEmail,
          initialFirstName: this.initialFirstName,
          initialLastName: this.initialLastName,
          initialPhoneNumber: this.initialPhoneNumber,
          initialAddress: this.initialAddress,
          allowedAddressCountries: this.allowedAddressCountries,
          allowedAddressStates: this.allowedAddressStates,
          allowedAddressCities: this.allowedAddressCities,
          installmentOptionsUrl: this.installmentOptionsUrl,
          postcodeOptionalCountries: this.postcodeOptionalCountries,
          onlyShowBankInstallment: this.onlyShowBankInstallment,
          enableAppleCheckout: this.enableAppleCheckout
        }
        this.payWindow.postMessage(orderDetails, '*');
        this.debugMsg("Sent Order details: ", orderDetails)
      }
    }
  }

  private async createTransaction(orderDetails: IOrderDetails = undefined) {
    try {
      let client_phone_number = undefined;
      try {
        const parsedNumber = parsePhoneNumber(this.initialPhoneNumber, this.countryCode)
        if (parsedNumber && parsedNumber.isValid()){
          client_phone_number = parsedNumber.number
        }
      } catch(err){
        console.log(err);
      }
      const modalLang = orderDetails?.locale ?? this.locale ?? 'en'
      const payload = {
        merchantID: this.merchantId,
        amount: this.amount,
        taxes: this.taxes,
        discount: this.discount,
        fees: this.fees,
        installmentOptionsUrl: this.installmentOptionsUrl,
        client_email: this.initialEmail,
        client_first_name: this.initialFirstName,
        client_last_name: this.initialLastName,
        client_phone_number,
        trx_lang: modalLang,
        address_details: this.initialAddress,
        ...(orderDetails ? {
          order_details: orderDetails
        } : undefined),
        ...(this.shippingMethods?.length == 1 ? {
          shipping: this.shippingMethods[0].price,
          shipping_details: this.shippingMethods[0]
        } : undefined),
        ...(this.refId ? { refId: this.refId, uniqueRef: this.uniqueRef } : undefined)
      };
      const response = await fetch(`${this.getApiUrl()}/transactions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Amwal': this.merchantId,
        },
        body: JSON.stringify({
          ...payload
        })
      });
      this.result = await response.json();
      if (!response.ok) {
        throw this.result.detail ?? response.statusText;
      }
      this.orderId = this.result.id;

    } catch (e) {
      console.log(e);
      this.orderError = {
        description: e.toString(),
      }
    }
    this.sendOrderDetails();
  }

  private async handleCheckout() {
    this.orderId = undefined;
    this.orderError = undefined;
    this.internallyDisabled = true;
    if (this.useModal) {
      await this.presentModal();
    } else {
      this.openPopup()
      document.body.classList.add("amwal-overlay-active");
      document.body.appendChild(this.el.querySelector('#amwal-overlay-id'));
      const timer = setInterval(() => {
        // all popup urls shouldn't have "Cross-Origin-Opener-Policy" header set otherwise window.closed will be true
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cross-Origin-Opener-Policy
        if (this.payWindow?.closed) {
          try {
            document.body.classList.remove("amwal-overlay-active");
            const overlayElement = document.body.removeChild(document.querySelector('body > #amwal-overlay-id'))
            this.el.querySelector('#amwal-overlay-template').appendChild(overlayElement);
            this.sendDismissEvent();
          } catch (err){
            console.log(err);
          } finally {
            clearInterval(timer);
          }
        }
      }, 200);
    }
    this.payWindow?.postMessage({ type: "amwal-lang", lang: this.locale }, '*');
    if (this.enablePreCheckoutTrigger) {
      this.waitingForPreCheckoutTriggerAck = true;
      this.amwalPreCheckoutTrigger.emit();
    } else {
      this.createTransaction();
    }
  }

  private async presentModal() {
    const div = document.createElement('div');
    div.innerHTML = `
    <iframe
      name="amwal-pay" allow="publickey-credentials-get; geolocation; payment"
      style="flex: 1; border: none;"
      src=${this.getPayUrl()}
      />
    `;

    this.checkoutModalEl = await modalController.create({
      component: div,
      canDismiss: false,
    });

    await this.checkoutModalEl.present();

    const iframeEl = document.getElementsByTagName("iframe").namedItem("amwal-pay");

    this.payWindow = iframeEl?.contentWindow;
  }

  private setSuccess(success: boolean) {
    this.paymentSuccessful = success;
    if (this.paymentSuccessful) {

      this.amwalCheckoutSuccess.emit({
        orderId: this.orderId,
      });

    }
  }

  @Method()
  async dismissModal() {
    if (this.checkoutModalEl) {
      this.checkoutModalEl.canDismiss = true;
      await modalController.dismiss();
      this.checkoutModalEl = undefined;
    }
    this.sendDismissEvent();
  }

  async sendDismissEvent() {
    if (this.orderId && !this.paymentSuccessful) {
      try {
        const response = await fetch(`${this.getApiUrl()}/transactions/${this.orderId}`);
        const order = await response.json();
        this.setSuccess(order.status === 'success');
      } catch (error) {
        console.error(error);
      };
    }
    this.internallyDisabled = false;
    this.amwalDismissed.emit({
      orderId: this.orderId,
      paymentSuccessful: this.paymentSuccessful
    });
    this.orderId = ""
    this.paymentSuccessful = false
  }

  @Listen('message', { target: 'window' })
  payWindowMessageHandler(ev: MessageEvent) {
    let match = true;
    try{
      const message = JSON.parse(ev.data);
      if (message.sender === "MF-3DSecure") {
        const url = message.url;
        if (url) {
          this.payWindow.location.href = url;
          return
        }
      }
    } catch (error){
      //console.log(error);
    }
    if (ev.data == 'query-dark-mode') {
      this.payWindow?.postMessage({
        type: 'dark-mode',
        darkMode: this.darkMode,
      }, '*')
    } else if (ev.data == 'query-lang') {
      this.payWindow?.postMessage({
        type: 'lang',
        lang: this.locale,
        showContinueShoppingButton: this.showContinueShoppingButton,
      }, '*')
    } else if (ev.data == 'query-amwal-order-id') {
      this.sendOrderDetails()
    } else if (ev.data === 'dismiss') {
      this.dismissModal();
    } else if (this.orderId) {
      //following messages result in emitted events. to avoid duplicate event firing, only button with orderId is triggered
      if (ev.data === 'paymentSuccessful') {
        this.setSuccess(true);
      } else if (ev.data === 'updateOrderOnPaymentsuccess') {
        this.updateOrderOnPaymentsuccess.emit({
          orderId: this.orderId,
        });
      } else if (ev.data.type === 'address-country-update') {
        this.waitingForAddressCountryAck = true;
        this.amwalAddressCountryUpdate.emit({
          ...ev.data
        });
      } else if (ev.data.type === 'address-state-update') {
        this.waitingForAddressStateAck = true;
        this.amwalAddressStateUpdate.emit({
          ...ev.data
        });
      } else if (ev.data.type == 'amwal-address-update') {
        this.waitingForAddressAck = true;
        this.amwalAddressUpdate.emit({
          ...ev.data.address,
          orderId: this.orderId
        });
      } else if (ev.data.type == 'amwal-pre-pay-trigger') {
        this.waitingForPrePayTriggerAck = true;
        this.amwalPrePayTrigger.emit(ev.data.transactionDetails);
      } else {
        match = false;
      }
    } else {
      match = false;
    }
    if (match) {
      this.debugMsg(ev);
    }
  }

  private waitingForAddressAck = false;

  @Listen('amwalAddressAck')
  addressAckHandler() {
    this.debugMsg("Address Ack Received");
    this.debugMsg("Current Shipping Methods: ", this.shippingMethods);
    this.debugMsg("Current Taxes: ", this.taxes);
    this.debugMsg("Current Discount: ", this.discount);
    this.debugMsg("Current Fees: ", this.fees);
    if (this.waitingForAddressAck) {
      this.waitingForAddressAck = false;
      this.payWindow?.postMessage({
        type: 'amwal-address-ack',
        shippingMethods: this.shippingMethods,
        taxes: this.taxes,
        discount: this.discount,
        discountDescription: this.discountDescription,
        fees: this.fees,
        feesDescription: this.feesDescription,
        amount: this.amount,
      }, '*')
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalAddressAck");
    }
  }

  @Listen('amwalAddressTriggerError')
  addressTriggerErrorHandler(event: CustomEvent<IErrorDescription>) {
    this.debugMsg("Address Trigger Error Received");
    if (this.waitingForAddressAck) {
      this.waitingForAddressAck = false;
      this.payWindow?.postMessage({
        type: 'amwal-address-trigger-error',
        ...event.detail,
      }, '*');
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalAddressTriggerError");
    }
  }

  private waitingForAddressCountryAck = false;
  @Listen('amwalAddressCountryAck')
  addressCountryAckHandler() {
    if (this.waitingForAddressCountryAck) {
      this.waitingForAddressCountryAck = false;
      this.payWindow?.postMessage({
        type: 'amwal-address-country-ack',
        allowedAddressStates: this.allowedAddressStates,
      }, '*')
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalAddressCountryAck");
    }
  }


  private waitingForAddressStateAck = false;
  @Listen('amwalAddressStateAck')
  addressStateAckHandler() {
    if (this.waitingForAddressStateAck) {
      this.waitingForAddressStateAck = false;
      this.payWindow?.postMessage({
        type: 'amwal-address-state-ack',
        allowedAddressCities: this.allowedAddressCities,
      }, '*')
    } else {
      this.debugMsg("Error: Unexpected or repeated amwalAddressStateAck");
    }
  }

  private openPopup() {
    this.payWindow?.close()
    const popupWidth = 500;
    const popupHeight = 860;
    const popupLeft = window.screenLeft + (screen.width - popupWidth) / 2;
    const popupTop = window.screenTop + (screen.height - popupHeight) / 4;
    const strWindowFeatures = `modal=yes, width=${popupWidth}, height=${popupHeight}, top=${popupTop}, left=${popupLeft}`;
    this.payWindow = window.open(this.getPayUrl(), 'amwal-pay', strWindowFeatures);
    this.payWindow.focus()
  }

  private checkBrowserType() {
    return this.agent.browser.name
  }

  private isApplePaySupported(){
    // const osName = this.agent.os.name;
    // const browserName = this.agent.browser.name;
    // console.log('browserName ', browserName, ' / osName ', osName)
    // return osName && browserName && (osName === 'iOS' || osName === 'Mac OS' || osName === 'iPadOS') && (browserName === 'Safari' || browserName === 'Chrome');
    try {
      return window.ApplePaySession?.canMakePayments();
    } catch (e){
      console.error(e);
    }
    return false;
  }

  private computeHideIcons(buttonWidth: number){
    if (buttonWidth){
      const ribbonShown = this.showDiscountRibbon && this.discount > 0 && this.amount > 0;
      const appleMaxWidth = ribbonShown ? 270 : 220;
      const nonAppleMaxWidth = ribbonShown ? 220 : 170;
      const newHideIcons =  buttonWidth < (this.isApplePaySupported() ? appleMaxWidth : nonAppleMaxWidth);
      if (this.hideIcons !== newHideIcons){
        this.hideIcons = newHideIcons
      }
    }
  }

  private formatNumber(value: number): number {
    return Number(value.toFixed(2));
  }

  private getNetAmount(): number {
    const netAmount = this.amount + (this.taxes ?? 0) + (this.fees ?? 0) - (this.discount ?? 0);
    return this.formatNumber(netAmount);
  }

  render() {
    return [
      <div>
        <link href='https://fonts.googleapis.com/css?family=Cairo' rel='stylesheet'></link>
        {this.paymentSuccessful ?
            <center>
              <ion-text>
                {this.t("Your Payment has been processed successfully!")}
              </ion-text>,
              <div style={{ width: '40px', height: '40px', margin: '10px' }} class={this.paymentSuccessful ? undefined : 'hidden'}>
                <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                     viewBox="0 0 50 50" style={{ "enable-background": "new 0 0 50 50;" }}>
                  <circle style={{ fill: "#25AE88" }} cx="25" cy="25" r="25" />
                  <polyline style={{
                    fill: 'none',
                    stroke: '#FFFFFF',
                    'stroke-width': '2',
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    'stroke-miterlimit': '10'
                  }} points="38,15 22,33 12,25" />
                </svg>
              </div>
            </center>
            :
            <center hidden={!this.showButton}>
              <amwal-installments-timeline
                  enable-installments={this.onlyShowBankInstallment}
                  installments-count={this.installmentsCount}
                  locale={this.locale}
                  amount={this.getNetAmount()}
                  timeline-style={this.timelineStyle}
                  footer-message={this.footerMessage}
              >
              </amwal-installments-timeline>
              <ion-button class={`amwal-checkout-button`} color="dark" onClick={() => this.handleCheckout()} disabled={this.disabled || this.internallyDisabled}>
                {
                    !this.hideIcons && this.showDiscountRibbon && this.discount > 0 && this.amount > 0 &&
                    <div class={'ribbon'}><span class={'ribbon-text'}>{this.t("OFF")} <br />
                      { this.formatNumber((this.discount / (this.amount + (this.taxes ?? 0) + (this.fees ?? 0))) * 100) + '%'}
                      </span></div>
                }
                <div class='payment-brand-class' slot={this.locale === 'ar' ? "start" : "end"} >
                  <div>{this.isApplePaySupported() && <img width={63} src={applePayLogo} />}<img width={60} src={cardLogo} /></div>
                </div>
                {this.locale === 'ar' && <div class="vl" ></div>}
                <div class='button-text'>{this.t(this.label)} </div>
                {this.locale === 'en' && <div class="vl" ></div>}
              </ion-button>
              {/* {
              this.showPaymentBrands &&
              <center class="payment-brand-class">
                <img class='mada-logo' src={madaLogo} />
                <img class='logo-white' src={visaLogo} />
                <img class='logo-white' src={masterLogo} />
                {
                  this.checkBrowserType().includes('Safari') &&
                  <img class='logo-white' src={applePayLogo} />
                }
              </center>
            } */}
            </center>
        }
      </div>,
      <template id="amwal-overlay-template">
        <div class="amwal-overlay" id="amwal-overlay-id">
          <div class="amwal-overlay-container">
            <span class="amwal-overlay-close" onClick={() => this.payWindow?.close()}>
              &#x2715;
            </span>
            <p>
              {this.t("Don't see your checkout window?")}
            </p>
            <a role="button" class="amwal-overlay-button" onClick={() => {
              if (this.checkBrowserType() === 'Safari' || !this.payWindow) {
                this.openPopup()
              } else {
                this.payWindow.focus()
              }
            }}>
              {this.t("Click here")}
            </a>
          </div>
        </div>
      </template>
    ];
  }
}